import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuestionsFirst } from "@/components/QuestionsFirst";
import { CITY_LIST } from "@/constants/cities";
import { COPY } from "@/constants/copy";

const POINTS = [
  { text: `${COPY.credit} Advice plus a straight estimate before any work starts.` },
  { text: COPY.rates },
  { text: "I treat your home like my own — shoe covers on, mess cleaned up" },
  { text: "Lifetime guarantee on my workmanship", sub: COPY.guarantee, testid: "about-guarantee-line" },
  { text: "If I can't handle the whole project — after 25 years of remodeling in Kitsap, I know the right tradespeople to recommend" },
];

export const About = ({ onBook }) => (
  <section id="about" data-testid="about-section" className="bg-secondary/50 py-16 sm:py-24">
    <div className="px-4 sm:px-8 lg:px-16 mx-auto max-w-6xl grid grid-cols-12 gap-y-8 gap-x-4 md:gap-10 lg:gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="col-span-12 md:col-span-5 relative min-w-0"
      >
        <div className="absolute -top-4 -left-4 w-full h-full bg-navy" aria-hidden="true" />
        <div className="relative overflow-hidden" data-testid="about-photo-frame">
          <img
            src="/bryan.jpg"
            alt="Bryan, owner of Solid Home Services"
            data-testid="about-photo"
            className="w-full h-[380px] sm:h-[440px] object-cover object-top"
            loading="lazy"
          />
          <div className="absolute bottom-0 inset-x-0 bg-navy/85 text-white px-5 py-3">
            <p className="font-display font-bold">Bryan</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
              Owner · 25 yrs remodeling in Kitsap
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="col-span-12 md:col-span-7 min-w-0"
      >
        <p className="overline-label mb-4">The one-man difference</p>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">
          When you book, you get me.
        </h2>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          I'm Bryan. Solid Home Services is my one-man shop — no call centers, no subcontractors,
          no hand-offs. The person who quotes your job is the person who shows up, does the work,
          and stands behind it for life. Twenty-five years of remodeling around Kitsap means that
          if your project outgrows one pair of hands, I'll point you to the right local tradespeople —
          no referral fees, no games.
        </p>
        <ul className="mt-8 space-y-3">
          {POINTS.map((p) => (
            <li key={p.text} className="flex items-start gap-3 text-foreground">
              <span className="h-1.5 w-1.5 bg-terracotta inline-block mt-2.5 shrink-0" />
              <span>
                {p.text}
                {p.sub && <span className="block text-sm text-muted-foreground mt-1" data-testid={p.testid}>{p.sub}</span>}
              </span>
            </li>
          ))}
        </ul>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4" data-testid="about-contact">
          <Button
            data-testid="about-book-button"
            data-book-cta
            onClick={() => onBook(null)}
            className="bg-terracotta hover:bg-terracotta-hover text-white font-semibold px-6 h-12 text-base hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow,background-color]"
          >
            Book online <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <QuestionsFirst />
        </div>
        <p className="mt-6 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-muted-foreground" data-testid="about-areas">
          <MapPin className="h-4 w-4 text-terracotta shrink-0" />
          <span>Serving</span>
          {CITY_LIST.map((c, i) => (
            <span key={c.slug}>
              <Link
                to={`/${c.slug}`}
                data-testid={`about-city-${c.slug}`}
                className="font-semibold text-navy hover:text-terracotta transition-colors underline decoration-dotted underline-offset-4"
              >
                {c.name}
              </Link>
              {i < CITY_LIST.length - 1 ? "," : ""}
            </span>
          ))}
          <span>&amp; all of North Kitsap County.</span>
        </p>
      </motion.div>
    </div>
  </section>
);
